import { useState } from 'react';
import { Field, useFormikContext } from 'formik';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandMore as ExpandIcon,
} from '@mui/icons-material';
import { Button, useModal, useToastr } from '@farmshare/ui-components';
import { UploadImageModal } from '../components/upload-image-modal';

export function SpecificationDescriptionsSection() {
  const [showHiddenSpecs, setShowHiddenSpecs] = useState(false);
  const [uploadModalSpecIndex, setUploadModalSpecIndex] = useState<
    number | null
  >(null);
  const { ask } = useModal();
  const { push } = useToastr();

  const { values, setFieldValue } = useFormikContext<{
    activeSpecification?: any;
  }>();
  const specifications = values.activeSpecification?.specifications || [];

  const displayedSpecifications = showHiddenSpecs
    ? specifications.map((spec: any, index: number) => ({
        spec,
        originalIndex: index,
      }))
    : specifications
        .map((spec: any, index: number) => ({ spec, originalIndex: index }))
        .filter(({ spec }: { spec: any }) => spec.isVisible !== false);
  const helperText = showHiddenSpecs
    ? 'Showing all specifications including hidden ones. Hidden specifications (marked with badge) will not appear to customers.'
    : 'Add descriptions for specifications and their options that will be shown to customers in the wizard.';

  return (
    <Accordion
      sx={{
        mb: 2,
        '&:before': {
          display: 'none',
        },
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '8px !important',
        boxShadow: 'none',
        '&:hover': {
          bgcolor: 'action.hover',
        },
        '&.Mui-expanded': {
          my: '16px !important',
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandIcon />}
        sx={{
          '&:hover': {
            bgcolor: 'rgba(0, 0, 0, 0.02)',
          },
          borderRadius: '8px',
          transition: 'background-color 0.2s ease',
        }}
      >
        <Typography variant="h6">Specification Descriptions</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <FormControlLabel
          control={
            <Switch
              checked={showHiddenSpecs}
              onChange={(e) => setShowHiddenSpecs(e.target.checked)}
            />
          }
          label="Show hidden specifications"
          sx={{ mb: 2 }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {helperText}
        </Typography>
        <Stack spacing={3}>
          {displayedSpecifications.map(
            ({ spec, originalIndex }: { spec: any; originalIndex: number }) => {
              const isHidden = spec.isVisible === false;

              return (
                <Box
                  key={spec._id || originalIndex}
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor:
                      isHidden && showHiddenSpecs ? 'grey.100' : 'grey.50',
                    p: 2.5,
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mb: 2 }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        color: 'text.primary',
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                      }}
                    >
                      {spec.name}
                    </Typography>
                    {isHidden && showHiddenSpecs && (
                      <Chip
                        label="Hidden"
                        size="small"
                        sx={{
                          bgcolor: 'grey.400',
                          color: 'white',
                          fontWeight: 500,
                          fontSize: '0.7rem',
                        }}
                      />
                    )}
                  </Stack>

                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                      alignItems: 'stretch',
                      height: '100%',
                    }}
                  >
                    {/* Image preview or Add Image button */}
                    <Box
                      sx={{
                        position: 'relative',
                        width: 120,
                        minWidth: 120,
                        border: 1,
                        borderColor: 'divider',
                        borderStyle:
                          spec.image || spec.imageUrl ? 'solid' : 'dashed',
                        borderRadius: 1,
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'stretch',
                        justifyContent: 'center',
                        bgcolor: 'background.paper',
                      }}
                    >
                      {spec.image || spec.imageUrl ? (
                        <>
                          <img
                            src={
                              spec.image
                                ? URL.createObjectURL(spec.image)
                                : spec.imageUrl
                            }
                            alt={spec.name}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: 4,
                              right: 4,
                              display: 'flex',
                              gap: 0.5,
                            }}
                          >
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setUploadModalSpecIndex(originalIndex);
                              }}
                              sx={{
                                bgcolor: 'background.paper',
                                '&:hover': {
                                  bgcolor: 'action.hover',
                                },
                                width: 28,
                                height: 28,
                                p: 0.5,
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                ask({
                                  type: 'ask',
                                  title: 'Remove Image',
                                  body: `Are you sure you want to remove the image for ${spec.name}?`,
                                  onConfirm: async () => {
                                    try {
                                      // Mark image for removal
                                      await setFieldValue(
                                        `activeSpecification.specifications.${originalIndex}.imageToRemove`,
                                        true,
                                      );
                                      // Clear image and imageUrl
                                      await setFieldValue(
                                        `activeSpecification.specifications.${originalIndex}.image`,
                                        undefined,
                                      );
                                      await setFieldValue(
                                        `activeSpecification.specifications.${originalIndex}.imageUrl`,
                                        undefined,
                                      );
                                    } catch (error) {
                                      push({
                                        title: 'Error',
                                        body:
                                          error instanceof Error
                                            ? error.message
                                            : 'Failed to remove image',
                                        severity: 'error',
                                        delay: 5000,
                                      });
                                    }
                                  },
                                });
                              }}
                              sx={{
                                bgcolor: 'background.paper',
                                '&:hover': {
                                  bgcolor: 'error.light',
                                  color: 'error.contrastText',
                                },
                                width: 28,
                                height: 28,
                                p: 0.5,
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </>
                      ) : (
                        <Button
                          onClick={() => {
                            setUploadModalSpecIndex(originalIndex);
                          }}
                          variant="outline-primary"
                          color="primary"
                          content="Add Image"
                          sx={{
                            m: 0,
                            borderStyle: 'dashed',
                            width: '100%',
                            height: '100%',
                            minHeight: '100%',
                          }}
                        />
                      )}
                    </Box>
                    <Field
                      name={`activeSpecification.specifications.${originalIndex}.description`}
                    >
                      {({ field }: any) => (
                        <TextField
                          {...field}
                          value={field.value ?? ''}
                          fullWidth
                          multiline
                          rows={4}
                          label="Specification Description"
                          placeholder={`Describe ${spec.name} for your customers...`}
                        />
                      )}
                    </Field>
                  </Box>

                  {spec.options && spec.options.length > 0 && (
                    <Box sx={{ pl: 3, mt: 2 }}>
                      <Stack spacing={2}>
                        {spec.options.map(
                          (option: any, optionIndex: number) => (
                            <Field
                              key={option._id || optionIndex}
                              name={`activeSpecification.specifications.${originalIndex}.options.${optionIndex}.description`}
                            >
                              {({ field }: any) => (
                                <TextField
                                  {...field}
                                  value={field.value ?? ''}
                                  fullWidth
                                  multiline
                                  rows={2}
                                  size="small"
                                  label={option.name}
                                  placeholder={`Describe ${option.name}...`}
                                  helperText="Help customers understand this option"
                                />
                              )}
                            </Field>
                          ),
                        )}
                      </Stack>
                    </Box>
                  )}
                </Box>
              );
            },
          )}
        </Stack>
      </AccordionDetails>
      {uploadModalSpecIndex !== null && (
        <UploadImageModal
          open
          onClose={() => setUploadModalSpecIndex(null)}
          specificationIndex={uploadModalSpecIndex}
          currentImageUrl={
            specifications[uploadModalSpecIndex]?.imageUrl ||
            (specifications[uploadModalSpecIndex]?.image
              ? URL.createObjectURL(specifications[uploadModalSpecIndex].image)
              : undefined)
          }
          onImageSelected={async (file: File, index: number) => {
            try {
              await setFieldValue(
                `activeSpecification.specifications.${index}.image`,
                file,
              );
              // Clear imageToRemove flag if set
              await setFieldValue(
                `activeSpecification.specifications.${index}.imageToRemove`,
                false,
              );
            } catch (error) {
              push({
                title: 'Error',
                body:
                  error instanceof Error
                    ? error.message
                    : 'Failed to set image',
                severity: 'error',
                delay: 5000,
              });
            }
          }}
        />
      )}
    </Accordion>
  );
}
